import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetUniqueDomains } from "@/hooks/use-mailbox";
import { useRunDnsCheck } from "@/hooks/use-postmaster";
import { AuthProvider, espTypeDescription } from "@/types/common";
import { useNavigate } from "@tanstack/react-router";
import { Globe, Mail } from "lucide-react";
import { useMemo, useState, useEffect } from "react";
import { toast } from "sonner";
import { SearchInput } from "../ui/search-input";

interface EmailTestOptionsProps {
  isOpen: boolean;
  onClose: () => void;
}

type TestMode = "initial" | "connected" | "unconnected";

const testOptions = [
  {
    title: "Connected Domains",
    description: "Test domains that are already connected to your account",
    value: "connected",
    icon: Globe,
  },
  {
    title: "Unconnected Domains",
    description: "Test domains that are not yet connected to your account",
    value: "unconnected",
    icon: Mail,
  },
];

export default function EmailTestOptions({
  isOpen,
  onClose,
}: EmailTestOptionsProps) {

  const [selectedEspType, setSelectedEspType] = useState<AuthProvider>(AuthProvider.SMTP);
  const [selectedDomain, setSelectedDomain] = useState<string>("");
  const [testMode, setTestMode] = useState<TestMode>("initial");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Get domains based on selected ESP type
  const { data: uniqueDomains } = useGetUniqueDomains(selectedEspType);
  const runDnsCheck = useRunDnsCheck(selectedDomain);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setTestMode("initial");
      setSelectedEspType(AuthProvider.SMTP);
      setSelectedDomain("");
      setSearchTerm("");
    }
  }, [isOpen]);

  // Reset selected domain when ESP type changes
  useEffect(() => {
    setSelectedDomain("");
    setSearchTerm("");
  }, [selectedEspType]);

  const filteredDomains = useMemo(() => {
    if (!searchTerm) return uniqueDomains;

    return uniqueDomains?.filter((domain) =>
      domain.toLowerCase().includes(searchTerm.toLowerCase())
    );
    // ?.slice(0, 10);
  }, [uniqueDomains, searchTerm]);

  const handleSelectDomain = (domain: string) => {
    setSelectedDomain(domain);
  };

  const handleRunTest = async () => {
    if (selectedDomain) {
      setLoading(true);
      try {
        // Pass both domain and espType to the API
        await runDnsCheck.mutateAsync({
          domain: selectedDomain,
          espType: selectedEspType,
        });
        toast.success(`DNS health check started for ${selectedDomain} using ${selectedEspType.replace('_', ' ')} configuration.`);
        window.cioanalytics?.track("email_health_test_run_connected", {
          from: "dialog",
          domain: selectedDomain,
          espType: selectedEspType,
        });
        onClose();
      } catch (err) {
        toast.error("Failed to start DNS health check for the domain.");
      } finally {
        setLoading(false);
      }
    } else {
      navigate({ to: `/domain-health/email-health-test` });
    }
  };

  const renderInitialOptions = () => (
    <RadioGroup
      className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4"
      onValueChange={(value) => {
        if (value === "unconnected") {
          navigate({ to: "/domain-health/email-health-test" });
        }
        setTestMode(value as TestMode);
      }}
    >
      {testOptions.map((option) => (
        <label className="relative flex cursor-pointer flex-col items-center gap-3 rounded-lg border text-center outline-offset-2 transition-colors has-[[data-disabled]]:cursor-not-allowed has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-accent has-[[data-disabled]]:opacity-50 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-ring/70 p-6">
          <RadioGroupItem
            id={`${option.value}`}
            value={option.value}
            className="sr-only after:absolute after:inset-0"
            disabled={false}
          />
          <option.icon className="size-4 shrink-0 text-blue-500" />
          <div className="text-center w-full text-wrap">
            <h3 className="text-sm font-medium">{option.title}</h3>
            <p className="text-sm font-normal text-muted-foreground mt-1">
              {option.description}
            </p>
          </div>
        </label>
      ))}
    </RadioGroup>
  );

  const renderConnectedDomains = () => (
    <div className="">
      {/* ESP Type Selection - Must be selected first */}
      <div className="mb-4">
        <Label htmlFor="esp-type" className="text-sm font-medium mb-2 block">
          Email Service Provider Type
        </Label>
        <Select value={selectedEspType} onValueChange={(value) => setSelectedEspType(value as AuthProvider)}>
          <SelectTrigger id="esp-type">
            <SelectValue placeholder="Select ESP type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={AuthProvider.SMTP}>SMTP - Standard SMTP Server</SelectItem>
            <SelectItem value={AuthProvider.GOOGLE}>Google - Gmail/Google Workspace</SelectItem>
            <SelectItem value={AuthProvider.MICROSOFT}>Microsoft - Outlook/Office 365</SelectItem>
            <SelectItem value={AuthProvider.AWS_API}>Amazon SES</SelectItem>
            <SelectItem value={AuthProvider.SENDGRID_API}>SendGrid</SelectItem>
            <SelectItem value={AuthProvider.GOOGLE_WORKSPACE_SMTP}>Google Workspace SMTP - Google Workspace SMTP</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">
          Select the email service provider type to see available domains
        </p>
      </div>

      {/* Domain Selection - Only show after ESP type is selected */}
      {selectedEspType && (
        <>
          <div className="px-4 py-2 bg-muted rounded-md mb-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Selected domain</div>
              <div className="text-sm">
                Total: {uniqueDomains?.length || 0} domains
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              {selectedDomain || "None selected"}
            </div>
            {selectedDomain && (
              <div className="text-xs text-blue-600 mt-1">
                ESP Type: <span className="font-medium">{espTypeDescription[selectedEspType as keyof typeof espTypeDescription]}</span>
              </div>
            )}
          </div>
          
          <SearchInput
            onSearch={setSearchTerm}
            placeholder="Search by domains..."
            className="max-w-none sm:w-full mb-3"
          />

          <div className="max-h-[250px] overflow-y-auto border rounded-md">
            {uniqueDomains && uniqueDomains.length > 0 ? (
              <RadioGroup value={selectedDomain} onValueChange={handleSelectDomain}>
                {filteredDomains?.map((domain: string) => (
                  <div
                    key={domain}
                    className="flex items-center p-3 border-b last:border-b-0"
                  >
                    <RadioGroupItem id={`domain-${domain}`} value={domain} />
                    <Label
                      htmlFor={`domain-${domain}`}
                      className="ml-3 flex-1 cursor-pointer"
                    >
                      {domain}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            ) : (
              <div className="p-4 text-center text-muted-foreground">
                No domains found for {selectedEspType.replace('_', ' ')} configuration
              </div>
            )}
          </div>
        </>
      )}

      {/* Show message when no ESP type is selected */}
      {!selectedEspType && (
        <div className="p-4 text-center text-muted-foreground border rounded-md">
          Please select an Email Service Provider type above to see available domains
        </div>
      )}
    </div>
  );

  const renderUnconnectedDomains = () => (
    <div className="space-y-4 mt-4">
      <div className="bg-blue-50 p-4 rounded-md">
        <h3 className="font-medium mb-2">Test Unconnected Domain</h3>
        <p className="text-sm text-blue-700">
          You can test any domain that is not connected to your account. This
          will help you verify email deliverability before connecting the
          domain.
        </p>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="">
        <DialogHeader>
          <DialogTitle>
            {testMode === "initial"
              ? "Choose Test Type"
              : testMode === "connected"
              ? "Test Connected Domain"
              : "Test Unconnected Domain"}
          </DialogTitle>
          <DialogDescription>
            {testMode === "initial"
              ? "Select how you want to run the domain health test"
              : testMode === "connected"
              ? "Select a domain to test from your connected accounts"
              : "Test a domain that is not connected to your account"}
          </DialogDescription>
        </DialogHeader>

        {testMode === "initial" && renderInitialOptions()}
        {testMode === "connected" && renderConnectedDomains()}
        {testMode === "unconnected" && renderUnconnectedDomains()}

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          {testMode !== "initial" && (
            <Button
              variant="outline"
              onClick={() => setTestMode("initial")}
              className="sm:mr-auto"
            >
              Back
            </Button>
          )}
          <Button
            variant="outline"
            onClick={onClose}
            className={testMode === "initial" ? "sm:mr-auto" : ""}
          >
            Cancel
          </Button>
          {testMode !== "initial" && (
            <Button
              onClick={handleRunTest}
              disabled={
                testMode === "connected" && (!selectedDomain || !selectedEspType || loading)
              }
              isLoading={loading}
            >
              {loading ? "Running..." : `Run ${selectedEspType.replace('_', ' ')} Test`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}